import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  CalendarMonth,
  ExpandMore,
  FileDownloadOutlined,
  FilterList,
  Percent,
  PrintOutlined,
  SummarizeOutlined,
  UnfoldMore,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { canUser } from '../utils/permissions';
import api from '../api/authApi';

const VIEW_MAIN = 'main';
const VIEW_REGISTER = 'register';
const VIEW_BANK_FILE = 'bank-file';
const VIEW_REBATE = 'rebate';

const btnGreenSx = (theme) => ({
  minWidth: 138,
  height: 34,
  borderRadius: 2,
  fontWeight: 700,
  fontSize: '0.82rem',
  textTransform: 'none',
  background: theme.primary,
  '&:hover': { background: theme.hover },
});

const headCellSx = {
  fontSize: '0.82rem',
  color: '#4b5563',
  fontWeight: 600,
  border: '1px solid #ececec',
  padding: '10px 10px',
  whiteSpace: 'nowrap',
  background: '#f9fafb',
};

const cellSx = {
  fontSize: '0.86rem',
  color: '#374151',
  border: '1px solid #efefef',
  padding: '10px 10px',
  whiteSpace: 'nowrap',
};

const f2 = (n) => Number(n || 0).toFixed(2);
const f3 = (n) => Number(n || 0).toFixed(3);
const n = (v) => {
  const x = parseFloat(v);
  return Number.isFinite(x) ? x : 0;
};
const toApiDate = (value) => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const [d, m, y] = value.split('/');
    return `${y}-${m}-${d}`;
  }
  return value;
};
const parseApi = (res) => {
  const body = res?.data ?? {};
  const success = body.success ?? body.Success ?? true;
  const payload = body.data ?? body.Data ?? null;
  const message = body.message ?? body.Message ?? '';
  if (!success) throw new Error(message || 'Operation failed.');
  return { payload, message };
};

const rowMatches = (row, search, keys) => {
  if (!search) return true;
  const q = search.toLowerCase();
  return keys.some((k) => String(row?.[k] ?? '').toLowerCase().includes(q));
};

function downloadCsv(filename, columns, rows) {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const head = columns.map((c) => esc(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => esc(c.value(r))).join(',')).join('\n');
  const csv = `${head}\n${body}`;
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function printRows(title, columns, rows) {
  const head = columns.map((c) => `<th>${c.label}</th>`).join('');
  const body = rows
    .map((r) => `<tr>${columns.map((c) => `<td>${String(c.value(r) ?? '')}</td>`).join('')}</tr>`)
    .join('');
  const html = `<!doctype html><html><head><meta charset="utf-8"/><title>${title}</title>
  <style>body{font-family:Arial;padding:16px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:7px 8px;font-size:12px}th{background:#f4f6fa}</style>
  </head><body><h2>${title}</h2><table><thead><tr>${head}</tr></thead><tbody>${body || `<tr><td colspan="${columns.length}">No Records Found.</td></tr>`}</tbody></table></body></html>`;
  const w = window.open('', '_blank', 'width=900,height=650');
  if (!w) return;
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 350);
}

function TableShell({
  theme,
  title,
  columns,
  rows,
  loading,
  totals,
  search,
  setSearch,
  pageSize,
  setPageSize,
  topLeftAction = null,
}) {
  return (
    <Card sx={{ borderRadius: 2, boxShadow: '0 1px 6px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', borderBottom: '1px solid #ededed', minHeight: 44 }}>
        <Box sx={{ flex: 1, px: 2, display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Typography sx={{ fontSize: '0.88rem', color: theme.primary, fontWeight: 700 }}>{title}</Typography>
          {topLeftAction}
        </Box>
        <Box sx={{ width: { xs: 150, md: 230 }, borderLeft: '1px solid #ededed', display: 'flex', alignItems: 'center', px: 2 }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            variant="standard"
            fullWidth
            InputProps={{ disableUnderline: true, sx: { fontSize: '0.82rem' } }}
          />
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key || col.label} style={{ ...headCellSx, textAlign: col.align || 'left', width: col.width }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                    <span>{col.label}</span>
                    {col.sortable && <UnfoldMore sx={{ fontSize: 15, color: '#81869a' }} />}
                  </Box>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={columns.length} style={{ ...cellSx, textAlign: 'center', padding: '24px 10px' }}>
                  <CircularProgress size={22} />
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} style={{ ...cellSx, textAlign: 'center', padding: '34px 10px', color: '#1f2937', fontWeight: 400 }}>
                  <Typography sx={{ fontSize: '0.9rem', color: '#9ca3af' }}>No Records Found.</Typography>
                </td>
              </tr>
            )}
            {!loading && rows.map((row, idx) => (
              <tr key={`${row.code || row.Code || 'r'}-${idx}`}>
                {columns.map((col) => (
                  <td key={`${col.key}-${idx}`} style={{ ...cellSx, textAlign: col.align || 'left' }}>
                    {col.render ? col.render(row, idx) : row[col.key] ?? ''}
                  </td>
                ))}
              </tr>
            ))}
            {!loading && totals && (
              <tr>
                {columns.map((col, idx) => (
                  <td key={`total-${col.key || idx}`} style={{ ...cellSx, fontWeight: 600, textAlign: col.align || 'left' }}>
                    {totals[col.key] ?? ''}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </Box>

      <Box sx={{ minHeight: 62, px: 2, borderTop: '1px solid #ececec', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.78rem', color: '#6b7280' }}>Showing {rows.length} entries</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#adb3c4', fontSize: '0.82rem' }}>
          <span>{'<<'}</span>
          <span>{'<'}</span>
          <span>{'>'}</span>
          <span>{'>>'}</span>
        </Box>
        <FormControl size="small" sx={{ minWidth: 90 }}>
          <Select value={pageSize} onChange={(e) => setPageSize(e.target.value)} sx={{ bgcolor: '#dbe3ee', borderRadius: 0, fontSize: '0.8rem', height: 30 }}>
            {[20, 50, 100].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Card>
  );
}

export default function AnnualBonus() {
  const { theme } = useApp();
  const { user } = useAuth();
  const canAdd = canUser(user, 'AnnualBonus', 'add');
  const [view, setView] = useState(VIEW_MAIN);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });
  const [printMenu, setPrintMenu] = useState(null);

  const [mainSearch, setMainSearch] = useState('');
  const [pendingSearch, setPendingSearch] = useState('');
  const [bankSearch, setBankSearch] = useState('');

  const [mainPageSize, setMainPageSize] = useState(20);
  const [pendingPageSize, setPendingPageSize] = useState(20);
  const [bankPageSize, setBankPageSize] = useState(50);

  const [periodKey, setPeriodKey] = useState('FY-2025-26');

  const [registerFrom, setRegisterFrom] = useState('01/12/2025');
  const [registerTo, setRegisterTo] = useState('28/11/2026');
  const [registerRebateType, setRegisterRebateType] = useState('');
  const [registerCowRebate, setRegisterCowRebate] = useState('0.00');
  const [registerBuffaloRebate, setRegisterBuffaloRebate] = useState('0.00');
  const [diffRebatePeriod, setDiffRebatePeriod] = useState(false);
  const [balanceDeduction, setBalanceDeduction] = useState(false);
  const [rebateOnlyDeducted, setRebateOnlyDeducted] = useState(false);

  const [rebateFrom, setRebateFrom] = useState('15/04/2026');
  const [rebateTo, setRebateTo] = useState('15/04/2026');
  const [rebateType, setRebateType] = useState('');
  const [cowBonus, setCowBonus] = useState('0.0');
  const [buffaloBonus, setBuffaloBonus] = useState('0.0');

  const [bankFrom, setBankFrom] = useState('01/12/2025');
  const [bankTo, setBankTo] = useState('28/11/2026');
  const [bankName, setBankName] = useState('Default B...');

  const [mainRows, setMainRows] = useState([]);
  const [pendingRows, setPendingRows] = useState([]);
  const [bankRows, setBankRows] = useState([]);

  const [loadingMain, setLoadingMain] = useState(false);
  const [loadingPending, setLoadingPending] = useState(false);
  const [loadingBank, setLoadingBank] = useState(false);
  const [loadingRebate, setLoadingRebate] = useState(false);

  const [mainTotals, setMainTotals] = useState({ totalRebate: 0, totalAnamat: 0, totalAmount: 0 });
  const [pendingTotals, setPendingTotals] = useState({ totalMilkQuantity: 0, totalRebate: 0, totalAnamat: 0, totalAmount: 0 });
  const [bankTotals, setBankTotals] = useState({ totalAmount: 0 });

  const periods = useMemo(
    () => ({
      'FY-2025-26': { label: '01 Dec 2025 - 28 Nov 2026', from: '2025-12-01', to: '2026-11-28' },
      'FY-2024-25': { label: '01 Dec 2024 - 30 Nov 2025', from: '2024-12-01', to: '2025-11-30' },
    }),
    []
  );

  const activePeriod = periods[periodKey] || periods['FY-2025-26'];

  const showToast = (msg, severity = 'success') => {
    setToast({ open: true, msg, severity });
  };

  const mapRows = useCallback((items) => (items || []).map((r) => ({
    farmerId: r.farmerId,
    code: r.code,
    name: r.name,
    totalMilkQuantity: n(r.totalMilkQuantity),
    rebate: n(r.rebate),
    anamat: n(r.anamat),
    amount: n(r.amount),
    bankName: r.bankName,
    bankBranch: r.bankBranch,
    accountNumber: r.accountNumber,
    ifscCode: r.ifscCode,
  })), []);

  const fetchMain = useCallback(async (generate = false) => {
    setLoadingMain(true);
    try {
      const req = {
        fromDate: activePeriod.from,
        toDate: activePeriod.to,
        cowRate: n(cowBonus || registerCowRebate),
        buffaloRate: n(buffaloBonus || registerBuffaloRebate),
        balanceDeduction: true,
        page: 1,
        pageSize: mainPageSize,
      };
      const res = generate
        ? await api.post('/annual-bonus/generate', req)
        : await api.get('/annual-bonus', { params: req });

      const { payload, message } = parseApi(res);
      const rows = mapRows(payload?.data);
      setMainRows(rows);
      setMainTotals({
        totalRebate: n(payload?.totalRebate),
        totalAnamat: n(payload?.totalAnamat),
        totalAmount: n(payload?.totalAmount),
      });
      if (generate) {
        showToast(message || (rows.length ? `Bonus generated for ${rows.length} farmer(s).` : 'No records.'), rows.length ? 'success' : 'error');
      }
    } catch (e) {
      showToast(e.message || 'Failed to load annual bonus.', 'error');
    } finally {
      setLoadingMain(false);
    }
  }, [activePeriod.from, activePeriod.to, cowBonus, registerCowRebate, buffaloBonus, registerBuffaloRebate, mainPageSize, mapRows]);

  const fetchPending = useCallback(async () => {
    setLoadingPending(true);
    try {
      const res = await api.get('/annual-bonus/bonus-register', {
        params: {
          fromDate: toApiDate(registerFrom),
          toDate: toApiDate(registerTo),
          differentRebatePeriod: diffRebatePeriod,
          rebateFromDate: diffRebatePeriod ? toApiDate(registerFrom) : null,
          rebateToDate: diffRebatePeriod ? toApiDate(registerTo) : null,
          balanceAmountDeduction: balanceDeduction,
          rebateToAnamatDeductedLitersOnly: rebateOnlyDeducted,
          rebateType: registerRebateType,
          cowRebate: n(registerCowRebate),
          buffaloRebate: n(registerBuffaloRebate),
          page: 1,
          pageSize: pendingPageSize,
        },
      });
      const { payload, message } = parseApi(res);
      const rows = mapRows(payload?.data);
      setPendingRows(rows);
      setPendingTotals({
        totalMilkQuantity: n(payload?.totalMilkQuantity),
        totalRebate: n(payload?.totalRebate),
        totalAnamat: n(payload?.totalAnamat),
        totalAmount: n(payload?.totalAmount),
      });
      showToast(message || (rows.length ? `Found ${rows.length} pending records.` : 'No pending records.'), rows.length ? 'success' : 'error');
    } catch (e) {
      showToast(e.message || 'Failed to load pending bonus.', 'error');
    } finally {
      setLoadingPending(false);
    }
  }, [registerFrom, registerTo, diffRebatePeriod, balanceDeduction, rebateOnlyDeducted, registerRebateType, registerCowRebate, registerBuffaloRebate, pendingPageSize, mapRows]);

  const fetchBank = useCallback(async () => {
    setLoadingBank(true);
    try {
      const res = await api.get('/annual-bonus/bank-payment-file', {
        params: {
          fromDate: toApiDate(bankFrom),
          toDate: toApiDate(bankTo),
          bankName,
          cowRate: n(cowBonus || registerCowRebate),
          buffaloRate: n(buffaloBonus || registerBuffaloRebate),
          page: 1,
          pageSize: bankPageSize,
        },
      });
      const { payload, message } = parseApi(res);
      const rows = mapRows(payload?.data);
      setBankRows(rows);
      setBankTotals({ totalAmount: n(payload?.totalAmount) });
      showToast(message || (rows.length ? `Loaded ${rows.length} bank rows.` : 'No bank records.'), rows.length ? 'success' : 'error');
      return rows;
    } catch (e) {
      showToast(e.message || 'Failed to load bank payment file.', 'error');
      return [];
    } finally {
      setLoadingBank(false);
    }
  }, [bankFrom, bankTo, bankName, cowBonus, registerCowRebate, buffaloBonus, registerBuffaloRebate, bankPageSize, mapRows]);

  const fetchRebate = useCallback(async () => {
    setLoadingRebate(true);
    try {
      const res = await api.get('/annual-bonus/rebate-calculation', {
        params: {
          fromDate: toApiDate(rebateFrom),
          toDate: toApiDate(rebateTo),
          bonusType: rebateType,
          cowBonus: n(cowBonus),
          buffaloBonus: n(buffaloBonus),
        },
      });
      const { payload, message } = parseApi(res);
      const cow = n(payload?.cowRebate);
      const buff = n(payload?.buffaloRebate);
      setCowBonus(f2(cow));
      setBuffaloBonus(f2(buff));
      showToast(message || `Total rebate ${f2(cow + buff)}`, 'success');
      return { cowRebate: cow, buffaloRebate: buff, totalRebate: n(payload?.totalRebate) };
    } catch (e) {
      showToast(e.message || 'Failed to calculate rebate.', 'error');
      return null;
    } finally {
      setLoadingRebate(false);
    }
  }, [rebateFrom, rebateTo, rebateType, cowBonus, buffaloBonus]);

  const tabBtnSx = (active) => ({
    color: active ? theme.primary : '#4b5563',
    px: 2.2,
    py: 1,
    fontSize: '0.85rem',
    borderRadius: 0,
    textTransform: 'none',
    fontWeight: active ? 700 : 500,
    borderLeft: '1px solid #ececec',
    '&:hover': { background: theme.light },
  });

  const mainColumns = useMemo(
    () => [
      { key: 'select', label: '', width: 42, render: () => <Checkbox size="small" /> },
      { key: 'code', label: 'Code', sortable: true },
      { key: 'name', label: 'Name' },
      { key: 'totalMilkQuantity', label: 'Total Milk Quantity', sortable: true, align: 'right', render: (r) => f3(r.totalMilkQuantity) },
      { key: 'rebate', label: 'Rebate', align: 'right', render: (r) => f2(r.rebate) },
      { key: 'anamat', label: 'Anamat', align: 'right', render: (r) => f2(r.anamat) },
      { key: 'amount', label: 'Amount', align: 'right', render: (r) => f2(r.amount) },
    ],
    []
  );

  const pendingColumns = useMemo(
    () => [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'totalMilkQuantity', label: 'Total Milk Quantity', align: 'right', render: (r) => f3(r.totalMilkQuantity) },
      { key: 'rebate', label: 'Rebate', align: 'right', render: (r) => f2(r.rebate) },
      { key: 'anamat', label: 'Anamat', align: 'right', render: (r) => f2(r.anamat) },
      { key: 'amount', label: 'Amount', align: 'right', render: (r) => f2(r.amount) },
    ],
    []
  );

  const bankColumns = useMemo(
    () => [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'accountNumber', label: 'Account No' },
      { key: 'ifscCode', label: 'IFSC' },
      { key: 'amount', label: 'Amount', align: 'right', render: (r) => f2(r.amount) },
    ],
    []
  );

  const topNav = (
    <Box sx={{ display: 'flex', borderBottom: '1px solid #ededed', flexWrap: 'wrap', minHeight: 58, bgcolor: '#fff' }}>
      <Box sx={{ px: 3, display: 'flex', alignItems: 'center', gap: 1.1, minWidth: 220 }}>
        <CalendarMonth sx={{ fontSize: 21, color: theme.primary }} />
        <Typography sx={{ color: theme.primary, fontSize: '1.05rem', fontWeight: 700, lineHeight: 1 }}>Annual Bonus</Typography>
      </Box>
      <Box sx={{ flex: 1 }} />
      <Button startIcon={<PrintOutlined sx={{ fontSize: 19 }} />} onClick={() => setView(VIEW_REGISTER)} sx={tabBtnSx(view === VIEW_REGISTER)}>
        Bonus Register
      </Button>
      <Button
        startIcon={<PrintOutlined sx={{ fontSize: 19 }} />}
        endIcon={<ExpandMore sx={{ fontSize: 20 }} />}
        onClick={(e) => setPrintMenu(e.currentTarget)}
        sx={tabBtnSx(false)}
      >
        Bank Payment Print
      </Button>
      <Button startIcon={<SummarizeOutlined sx={{ fontSize: 20 }} />} onClick={() => setView(VIEW_BANK_FILE)} sx={tabBtnSx(view === VIEW_BANK_FILE)}>
        Bank Payment File
      </Button>
      <Button startIcon={<Percent sx={{ fontSize: 20 }} />} onClick={() => setView(VIEW_REBATE)} sx={tabBtnSx(view === VIEW_REBATE)}>
        Rebate Calculation
      </Button>
    </Box>
  );

  return (
    <Layout title="Annual Bonus">
      <Card sx={{ borderRadius: 2.3, boxShadow: '0 2px 10px rgba(0,0,0,0.06)', overflow: 'hidden', background: '#fff' }}>
        {view === VIEW_MAIN && topNav}

        {view === VIEW_MAIN && (
          <Box sx={{ p: { xs: 1.4, md: 2 }, bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.6, mb: 1.9 }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Date Range :</Typography>
              <FormControl size="small" sx={{ minWidth: 315 }}>
                <Select value={periodKey} onChange={(e) => setPeriodKey(e.target.value)} sx={{ fontSize: '0.84rem', height: 36 }}>
                  {Object.entries(periods).map(([k, p]) => (
                    <MenuItem key={k} value={k}>{p.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ flex: 1 }} />
              <Button variant="outlined" onClick={() => fetchMain(false)} sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.82rem', borderColor: theme.primary, color: theme.primary }}>
                Load
              </Button>
              {canAdd && <Button variant="contained" onClick={() => fetchMain(true)} sx={btnGreenSx(theme)}>
                Generate Bonus
              </Button>}
            </Box>

            <TableShell
              theme={theme}
              title="Annual Bonus"
              columns={mainColumns}
              rows={mainRows.filter((r) => rowMatches(r, mainSearch, ['code', 'name', 'accountNumber']))}
              loading={loadingMain}
              totals={{
                select: '',
                code: '',
                name: '',
                totalMilkQuantity: 'Total',
                rebate: f2(mainSearch ? mainRows.filter((r) => rowMatches(r, mainSearch, ['code', 'name', 'accountNumber'])).reduce((s, r) => s + n(r.rebate), 0) : mainTotals.totalRebate),
                anamat: f2(mainSearch ? mainRows.filter((r) => rowMatches(r, mainSearch, ['code', 'name', 'accountNumber'])).reduce((s, r) => s + n(r.anamat), 0) : mainTotals.totalAnamat),
                amount: f2(mainSearch ? mainRows.filter((r) => rowMatches(r, mainSearch, ['code', 'name', 'accountNumber'])).reduce((s, r) => s + n(r.amount), 0) : mainTotals.totalAmount),
              }}
              search={mainSearch}
              setSearch={setMainSearch}
              pageSize={mainPageSize}
              setPageSize={setMainPageSize}
            />
          </Box>
        )}

        {view === VIEW_REGISTER && (
          <Box sx={{ p: { xs: 1.4, md: 2.2 }, bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, borderBottom: '1px solid #efefef', pb: 1.2 }}>
              <IconButton size="small" onClick={() => setView(VIEW_MAIN)} sx={{ color: theme.primary }}>
                <ArrowBack />
              </IconButton>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: theme.primary }}>Pending Bonus</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '0.85rem', color: '#111827', mb: 0.8 }}>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Anamat Period :
                </Box>{' '}
                <Box component="span" sx={{ color: '#b08914', fontSize: '0.8rem' }}>
                  (From and To Date should be as per the Billing Cycle)
                </Box>
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>From Date :</Typography>
                <TextField size="small" value={registerFrom} onChange={(e) => setRegisterFrom(e.target.value)} sx={{ width: 230, '& .MuiInputBase-input': { fontSize: '0.84rem' } }} />
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>To Date :</Typography>
                <TextField size="small" value={registerTo} onChange={(e) => setRegisterTo(e.target.value)} sx={{ width: 230, '& .MuiInputBase-input': { fontSize: '0.84rem' } }} />
              </Box>

              <Box sx={{ mt: 1.4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox checked={diffRebatePeriod} onChange={(e) => setDiffRebatePeriod(e.target.checked)} />
                  <Typography sx={{ fontSize: '0.84rem', color: '#374151' }}>Different Rebate Period</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox checked={balanceDeduction} onChange={(e) => setBalanceDeduction(e.target.checked)} />
                  <Typography sx={{ fontSize: '0.84rem', color: '#374151' }}>Balance Amount Deduction</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox checked={rebateOnlyDeducted} onChange={(e) => setRebateOnlyDeducted(e.target.checked)} />
                  <Typography sx={{ fontSize: '0.84rem', color: '#374151' }}>Rebate to the Anamat Deducted liters only</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Rebate Type :</Typography>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <Select value={registerRebateType} onChange={(e) => setRegisterRebateType(e.target.value)} displayEmpty sx={{ fontSize: '0.84rem', height: 36 }}>
                    <MenuItem value="">Tap To Select</MenuItem>
                    <MenuItem value="by-liter">By Liter</MenuItem>
                    <MenuItem value="by-amount">By Amount</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2.6, mt: 1.8 }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Cow Rebate :</Typography>
                <TextField size="small" value={registerCowRebate} onChange={(e) => setRegisterCowRebate(e.target.value)} sx={{ width: 110, '& .MuiInputBase-input': { fontSize: '0.84rem' } }} />
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Buffalo Rebate :</Typography>
                <TextField size="small" value={registerBuffaloRebate} onChange={(e) => setRegisterBuffaloRebate(e.target.value)} sx={{ width: 110, '& .MuiInputBase-input': { fontSize: '0.84rem' } }} />
                <Button variant="contained" onClick={fetchPending} sx={btnGreenSx(theme)}>
                  Find
                </Button>
              </Box>
            </Box>

            <TableShell
              theme={theme}
              title={`Pending Bonus - ${pendingRows.filter((r) => rowMatches(r, pendingSearch, ['code', 'name'])).length}`}
              columns={pendingColumns}
              rows={pendingRows.filter((r) => rowMatches(r, pendingSearch, ['code', 'name']))}
              loading={loadingPending}
              totals={{
                code: '',
                name: 'Total',
                totalMilkQuantity: f3(pendingSearch ? pendingRows.filter((r) => rowMatches(r, pendingSearch, ['code', 'name'])).reduce((s, r) => s + n(r.totalMilkQuantity), 0) : pendingTotals.totalMilkQuantity),
                rebate: f2(pendingSearch ? pendingRows.filter((r) => rowMatches(r, pendingSearch, ['code', 'name'])).reduce((s, r) => s + n(r.rebate), 0) : pendingTotals.totalRebate),
                anamat: f2(pendingSearch ? pendingRows.filter((r) => rowMatches(r, pendingSearch, ['code', 'name'])).reduce((s, r) => s + n(r.anamat), 0) : pendingTotals.totalAnamat),
                amount: f2(pendingSearch ? pendingRows.filter((r) => rowMatches(r, pendingSearch, ['code', 'name'])).reduce((s, r) => s + n(r.amount), 0) : pendingTotals.totalAmount),
              }}
              search={pendingSearch}
              setSearch={setPendingSearch}
              pageSize={pendingPageSize}
              setPageSize={setPendingPageSize}
            />
          </Box>
        )}

        {view === VIEW_BANK_FILE && (
          <Box sx={{ p: { xs: 1.4, md: 2.2 }, bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, borderBottom: '1px solid #efefef', pb: 1.2 }}>
              <IconButton size="small" onClick={() => setView(VIEW_MAIN)} sx={{ color: theme.primary }}>
                <ArrowBack />
              </IconButton>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: theme.primary }}>Annual Bonus Bank Payment File</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.7, mb: 2 }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>From Date :</Typography>
              <TextField size="small" value={bankFrom} onChange={(e) => setBankFrom(e.target.value)} sx={{ width: 165, '& .MuiInputBase-input': { fontSize: '0.84rem' } }} />
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>To Date :</Typography>
              <TextField size="small" value={bankTo} onChange={(e) => setBankTo(e.target.value)} sx={{ width: 165, '& .MuiInputBase-input': { fontSize: '0.84rem' } }} />
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Select Bank:</Typography>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <Select value={bankName} onChange={(e) => setBankName(e.target.value)} sx={{ fontSize: '0.84rem', height: 36 }}>
                  <MenuItem value="Default B...">Default B...</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ flex: 1 }} />
              <Button variant="contained" onClick={fetchBank} sx={btnGreenSx(theme)}>
                Preview
              </Button>
              <Button
                variant="contained"
                onClick={async () => {
                  const rows = bankRows.length ? bankRows : await fetchBank();
                  if (!rows.length) return;
                  downloadCsv(
                    'annual_bonus_bank_file.csv',
                    [
                      { label: 'Code', value: (r) => r.code },
                      { label: 'Name', value: (r) => r.name },
                      { label: 'Account Number', value: (r) => r.accountNumber },
                      { label: 'IFSC', value: (r) => r.ifscCode },
                      { label: 'Amount', value: (r) => f2(r.amount) },
                    ],
                    rows
                  );
                  showToast('Bank file downloaded.');
                }}
                sx={btnGreenSx(theme)}
              >
                Download Excel
              </Button>
            </Box>

            <TableShell
              theme={theme}
              title=""
              columns={bankColumns}
              rows={bankRows.filter((r) => rowMatches(r, bankSearch, ['code', 'name', 'accountNumber', 'bankName']))}
              loading={loadingBank}
              totals={{
                code: '',
                name: 'Total',
                accountNumber: '',
                ifscCode: '',
                amount: f2(bankSearch ? bankRows.filter((r) => rowMatches(r, bankSearch, ['code', 'name', 'accountNumber', 'bankName'])).reduce((s, r) => s + n(r.amount), 0) : bankTotals.totalAmount),
              }}
              search={bankSearch}
              setSearch={setBankSearch}
              pageSize={bankPageSize}
              setPageSize={setBankPageSize}
              topLeftAction={
                <Button
                  variant="outlined"
                  startIcon={<PrintOutlined sx={{ fontSize: 16 }} />}
                  onClick={async () => {
                    const rows = bankRows.length ? bankRows : await fetchBank();
                    printRows(
                      'Annual Bonus Bank Payment',
                      [
                        { label: 'Code', value: (r) => r.code },
                        { label: 'Name', value: (r) => r.name },
                        { label: 'Account', value: (r) => r.accountNumber },
                        { label: 'IFSC', value: (r) => r.ifscCode },
                        { label: 'Amount', value: (r) => f2(r.amount) },
                      ],
                      rows
                    );
                  }}
                  sx={{ textTransform: 'none', borderRadius: 1.5, color: theme.primary, borderColor: theme.border, fontSize: '0.8rem', '&:hover': { borderColor: theme.primary, background: theme.light } }}
                >
                  Print
                </Button>
              }
            />
          </Box>
        )}

        {view === VIEW_REBATE && (
          <Box sx={{ p: { xs: 1.4, md: 2.2 }, bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, borderBottom: '1px solid #efefef', pb: 1.2 }}>
              <IconButton size="small" onClick={() => setView(VIEW_MAIN)} sx={{ color: theme.primary }}>
                <ArrowBack />
              </IconButton>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: theme.primary }}>Rebate Calculation</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.8, mb: 2 }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>From Date :</Typography>
              <TextField size="small" value={rebateFrom} onChange={(e) => setRebateFrom(e.target.value)} sx={{ width: 160, '& .MuiInputBase-input': { fontSize: '0.84rem' } }} />
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>To Date :</Typography>
              <TextField size="small" value={rebateTo} onChange={(e) => setRebateTo(e.target.value)} sx={{ width: 160, '& .MuiInputBase-input': { fontSize: '0.84rem' } }} />
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Bonus Type :</Typography>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select value={rebateType} onChange={(e) => setRebateType(e.target.value)} displayEmpty sx={{ fontSize: '0.84rem', height: 36 }}>
                  <MenuItem value="">Tap To Select</MenuItem>
                  <MenuItem value="cow">Cow</MenuItem>
                  <MenuItem value="buffalo">Buffalo</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.7 }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Cow Bonus :</Typography>
              <TextField size="small" value={cowBonus} onChange={(e) => setCowBonus(e.target.value)} sx={{ width: 160, '& .MuiInputBase-input': { fontSize: '0.84rem' } }} />
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Buffalo Bonus :</Typography>
              <TextField size="small" value={buffaloBonus} onChange={(e) => setBuffaloBonus(e.target.value)} sx={{ width: 160, '& .MuiInputBase-input': { fontSize: '0.84rem' } }} />
              <Button variant="contained" onClick={fetchRebate} sx={btnGreenSx(theme)}>
                {loadingRebate ? 'Finding...' : 'Find'}
              </Button>
              <Button
                variant="contained"
                onClick={async () => {
                  const r = await fetchRebate();
                  if (!r) return;
                  downloadCsv(
                    'annual_bonus_rebate.csv',
                    [
                      { label: 'From Date', value: () => rebateFrom },
                      { label: 'To Date', value: () => rebateTo },
                      { label: 'Cow Rebate', value: () => f2(r.cowRebate) },
                      { label: 'Buffalo Rebate', value: () => f2(r.buffaloRebate) },
                      { label: 'Total Rebate', value: () => f2(r.totalRebate) },
                    ],
                    [{}]
                  );
                  showToast('Rebate report downloaded.');
                }}
                sx={btnGreenSx(theme)}
              >
                Download Excel
              </Button>
            </Box>
          </Box>
        )}
      </Card>

      <Menu
        anchorEl={printMenu}
        open={Boolean(printMenu)}
        onClose={() => setPrintMenu(null)}
        PaperProps={{ sx: { minWidth: 200, borderRadius: 1.6, mt: 0.5 } }}
      >
        <MenuItem
          onClick={async () => {
            setPrintMenu(null);
            const rows = await fetchBank();
            printRows(
              'Annual Bonus Bank Payment Print',
              [
                { label: 'Code', value: (r) => r.code },
                { label: 'Name', value: (r) => r.name },
                { label: 'Account', value: (r) => r.accountNumber },
                { label: 'IFSC', value: (r) => r.ifscCode },
                { label: 'Amount', value: (r) => f2(r.amount) },
              ],
              rows
            );
          }}
          sx={{ py: 1.2, fontSize: '0.84rem' }}
        >
          <PrintOutlined sx={{ mr: 1.2, color: theme.primary }} />
          Print
        </MenuItem>
        <MenuItem
          onClick={async () => {
            setPrintMenu(null);
            const rows = await fetchBank();
            if (!rows.length) return;
            downloadCsv(
              'annual_bonus_bank_payment_print.csv',
              [
                { label: 'Code', value: (r) => r.code },
                { label: 'Name', value: (r) => r.name },
                { label: 'Account', value: (r) => r.accountNumber },
                { label: 'IFSC', value: (r) => r.ifscCode },
                { label: 'Amount', value: (r) => f2(r.amount) },
              ],
              rows
            );
            showToast('Bank payment print file downloaded.');
          }}
          sx={{ py: 1.2, fontSize: '0.84rem' }}
        >
          <FileDownloadOutlined sx={{ mr: 1.2, color: theme.primary }} />
          Download Excel
        </MenuItem>
      </Menu>

      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          icon={<FilterList fontSize="inherit" />}
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          sx={{
            width: '100%',
            minWidth: 250,
            fontWeight: 600,
            fontSize: '0.84rem',
            bgcolor: toast.severity === 'error' ? '#cf4d5c' : theme.primary,
          }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Layout>
  );
}
